import type {
  Bible,
  CanonicalBook,
  Verse,
  SearchVerse,
  SearchResponse,
  Comment,
  CommentCounts,
  AuthHeaders,
  Bookmark,
  HighlightResponse,
  VerseHighlightsPayload,
} from '../types';
import { expireSession, getStoredToken } from './session';
import { setContentVersion, versionParam } from './contentVersion';

const API_BASE_URL = '/api';
const CONTENT_VERSION_HEADER = 'X-Content-Version';

function getAuthToken(): string | null {
  return getStoredToken();
}

function getAuthHeaders(): AuthHeaders {
  const token = getAuthToken();
  if (!token) return {};
  return {
    'Authorization': `Bearer ${token}`,
  };
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  // Every response carries the current content version; picking it up here is how the app learns to
  // pin its content URLs. The API is same-origin, so the header is readable without CORS exposure.
  setContentVersion(response.headers.get(CONTENT_VERSION_HEADER));

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

async function fetchApiAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const authHeaders = getAuthHeaders();

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
  };

  const mergedOptions: RequestInit = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  const response = await fetch(url, mergedOptions);

  if (!response.ok) {
    if (response.status === 401) {
      // The token is gone or no longer accepted: drop the session rather than leaving the UI
      // showing a logged-in user whose every write will fail.
      expireSession();
      throw new Error('Your session has expired - please login again');
    }
    if (response.status === 403) {
      throw new Error('Forbidden - admin access required');
    }
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return null as T;
}

export const bibleApi = {
  getAll: (): Promise<Bible[]> => fetchApi<Bible[]>('/bibles'),
};

export const booksApi = {
  getAll: (): Promise<CanonicalBook[]> => fetchApi<CanonicalBook[]>('/books'),
};

// The content endpoints take the current content version, which pins the URL and lets the API mark
// the response immutable. Passing nothing still works — the response is then simply not cacheable.
export const verseApi = {
  getByBook: (bibleId: number, canonicalBookId: number, version = ''): Promise<Verse[]> =>
    fetchApi<Verse[]>(`/bibles/${bibleId}/books/${canonicalBookId}/verses${versionParam(version)}`),

  getChapters: (bibleId: number, canonicalBookId: number, version = ''): Promise<number[]> =>
    fetchApi<number[]>(`/bibles/${bibleId}/books/${canonicalBookId}/chapters${versionParam(version)}`),

  // Only the books this translation actually contains (some imports are partial).
  getBooksForBible: (bibleId: number, version = ''): Promise<CanonicalBook[]> =>
    fetchApi<CanonicalBook[]>(`/bibles/${bibleId}/books${versionParam(version)}`),

  // The API wraps the hits in an envelope ({query, translation, totalResults, verses}), unlike the
  // other verse endpoints, which return bare arrays. Unwrap here so callers get a plain list.
  search: async (query: string, translation: string | null = null): Promise<SearchVerse[]> => {
    const params = new URLSearchParams({ q: query });
    if (translation) {
      params.append('translation', translation);
    }
    const response = await fetchApi<SearchResponse>(`/search?${params.toString()}`);
    return response.verses ?? [];
  },
};

export const commentApi = {
  // verseNumber -> number of comments, for one chapter. Only commented verses appear in the map, so
  // marking a chapter costs a single request instead of one lookup per verse.
  getCountsByChapter: (canonicalBookId: number, chapterNumber: number): Promise<CommentCounts> =>
    fetchApi<CommentCounts>(`/comments/${canonicalBookId}/${chapterNumber}/counts`),

  getByVerse: (canonicalBookId: number, chapterNumber: number, verseNumber: number): Promise<Comment[]> =>
    fetchApi<Comment[]>(`/comments/${canonicalBookId}/${chapterNumber}/${verseNumber}`),

  create: (canonicalBookId: number, chapterNumber: number, verseNumber: number, author: string, text: string): Promise<Comment> =>
    fetchApiAuth<Comment>('/comments', {
      method: 'POST',
      body: JSON.stringify({
        canonicalBookId,
        chapterNumber,
        verseNumber,
        author,
        text,
      }),
    }),

  update: (id: number, text: string): Promise<Comment> =>
    fetchApiAuth<Comment>(`/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    }),

  delete: (id: number): Promise<null> =>
    fetchApiAuth<null>(`/comments/${id}`, {
      method: 'DELETE',
    }),
};

// Highlights and bookmarks belong to the logged-in user, so even reads go through the authenticated wrapper.
export const highlightApi = {
  getByChapter: (bibleId: number, canonicalBookId: number, chapterNumber: number): Promise<HighlightResponse[]> =>
    fetchApiAuth<HighlightResponse[]>(`/highlights/${bibleId}/${canonicalBookId}/${chapterNumber}`),

  // Replaces the highlights of each listed verse; verses not listed are left alone.
  replace: (verses: VerseHighlightsPayload[]): Promise<null> =>
    fetchApiAuth<null>('/highlights', {
      method: 'PUT',
      body: JSON.stringify({ verses }),
    }),
};

export const bookmarkApi = {
  getAll: (): Promise<Bookmark[]> => fetchApiAuth<Bookmark[]>('/bookmarks'),

  // Bookmarking a verse that is already bookmarked returns the existing bookmark.
  create: (canonicalBookId: number, chapterNumber: number, verseNumber: number): Promise<Bookmark> =>
    fetchApiAuth<Bookmark>('/bookmarks', {
      method: 'POST',
      body: JSON.stringify({ canonicalBookId, chapterNumber, verseNumber }),
    }),

  delete: (id: number): Promise<null> =>
    fetchApiAuth<null>(`/bookmarks/${id}`, {
      method: 'DELETE',
    }),
};

export const translationApi = {
  delete: (translationId: number): Promise<null> =>
    fetchApiAuth<null>(`/system/translations/${translationId}`, {
      method: 'DELETE',
    }),
};

export interface ApiVersion {
  version: string;
  commit: string;
  branch: string;
  commitTime: string | null;
  buildTime: string | null;
}

export const systemApi = {
  getVersion: (): Promise<ApiVersion> => fetchApi<ApiVersion>('/system/version'),

  importBible: (file: File, authHeaders: Record<string, string> = {}): Promise<unknown> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          window.dispatchEvent(
            new CustomEvent('bibleUploadProgress', {
              detail: { progress: percentComplete }
            })
          );
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error('Invalid JSON response from server'));
          }
        } else if (xhr.status === 401) {
          expireSession();
          reject(new Error('Your session has expired - please login again'));
        } else if (xhr.status === 403) {
          reject(new Error('Admin access required'));
        } else if (xhr.status === 400) {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.message || 'Invalid XML format'));
          } catch {
            reject(new Error('Invalid XML format'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = () => {
        reject(new Error('Upload cancelled'));
      };

      xhr.open('POST', `${API_BASE_URL}/system/import`);
      Object.entries(authHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.send(formData);
    });
  },
};
